/**
 * @hostesa/feature-flags — React integration
 *
 * Provides a React context, hooks, and a gate component for consuming
 * evaluated feature flags throughout the app tree.
 *
 * IMPORTANT: Feature flags are NOT an authorization layer.
 * A route is accessible only when session + RBAC + ABAC + flag ALL pass.
 * Use FeatureGate and useFlag for UX-level hiding only. The backend is the
 * security boundary; a disabled flag is a convenience redirect, not access
 * control.
 *
 * Typical usage:
 *   1. At app bootstrap, resolve flags via createFlagEvaluator.
 *   2. Wrap the app (or hotel-scoped route subtree) with <FeatureFlagProvider>.
 *   3. Consume flags with useFlag('flag_name') or <FeatureGate flag="flag_name">.
 */

import { createContext, type JSX, type ReactNode, useContext } from 'react';
import type { FlagEvaluator } from './evaluate.js';
import { flagGate } from './evaluate.js';
import type { FeatureFlag, FlagsRecord } from './flags.js';
import { DEFAULT_FLAGS } from './flags.js';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface FeatureFlagContextValue {
  /** The fully resolved flags map for the current hotel/environment context. */
  flags: FlagsRecord;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: DEFAULT_FLAGS,
});

FeatureFlagContext.displayName = 'FeatureFlagContext';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface FeatureFlagProviderProps {
  /**
   * The resolved flags map for the current context.
   * Obtain this by calling evaluator.resolve(hotelId) and passing the result.
   *
   * The provider is intentionally decoupled from the evaluator so that apps
   * can apply hotel/environment resolution in a route loader before rendering.
   */
  flags: FlagsRecord;
  children: ReactNode;
}

/**
 * Provides resolved feature flags to the component subtree.
 *
 * Place this at the hotel-scoped route boundary so that flag values are
 * always resolved for the correct hotel context before any child renders.
 *
 * @example
 * ```tsx
 * // In a route loader or beforeLoad:
 * const flags = evaluator.resolve(hotelId);
 *
 * // In the route component:
 * <FeatureFlagProvider flags={flags}>
 *   <HotelDashboard />
 * </FeatureFlagProvider>
 * ```
 */
export function FeatureFlagProvider({ flags, children }: FeatureFlagProviderProps): JSX.Element {
  return <FeatureFlagContext.Provider value={{ flags }}>{children}</FeatureFlagContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the resolved boolean value of a single feature flag from context.
 *
 * @param flag - The flag to evaluate.
 * @returns `true` if the flag is enabled in the current context, `false` otherwise.
 *
 * @example
 * ```tsx
 * const inStayEnabled = useFlag('in_stay_operations');
 * if (!inStayEnabled) return null;
 * ```
 */
export function useFlag(flag: FeatureFlag): boolean {
  const { flags } = useContext(FeatureFlagContext);
  return flags[flag];
}

/**
 * Returns the entire resolved flags map from context.
 * Prefer useFlag for individual flag reads in components to keep re-renders minimal.
 *
 * @returns The full FlagsRecord for the current context.
 */
export function useFlags(): FlagsRecord {
  const { flags } = useContext(FeatureFlagContext);
  return flags;
}

// ---------------------------------------------------------------------------
// Gate component
// ---------------------------------------------------------------------------

export interface FeatureGateProps {
  /** The flag that must be enabled for children to render. */
  flag: FeatureFlag;
  /**
   * Content to render when the flag is disabled.
   * Defaults to null (nothing rendered). Pass a redirect component or a
   * fallback UI as appropriate for the route context.
   *
   * IMPORTANT: Hiding via fallback is a UX convenience.
   * The route guard (beforeLoad) must ALSO enforce the flag to prevent
   * direct navigation to disabled surfaces.
   */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders children when the given flag is enabled; renders fallback otherwise.
 *
 * Use this for conditional UI sections (e.g. hiding a nav item or a tab).
 * For route-level blocking, also add a beforeLoad guard using flagGate().
 *
 * @example
 * ```tsx
 * <FeatureGate flag="in_stay_operations" fallback={<Navigate to="/reception" />}>
 *   <OperationsBoard />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({ flag, fallback = null, children }: FeatureGateProps): JSX.Element {
  const enabled = useFlag(flag);
  return <>{enabled ? children : fallback}</>;
}

// ---------------------------------------------------------------------------
// Re-export flagGate for convenience (non-React route guards)
// ---------------------------------------------------------------------------

export type { FlagEvaluator };
/**
 * Re-exported from evaluate.ts for single-import convenience.
 * Use in TanStack Router beforeLoad guards alongside session/RBAC/ABAC checks.
 *
 * IMPORTANT: A route is accessible only when session + RBAC + ABAC + flag ALL pass.
 * flagGate only checks the flag layer. The calling guard must enforce the rest.
 */
export { flagGate };
